import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useFocusTrap } from '@voll-intelligence/intelligence-ui-kit';
import { useGenUiI18n } from '../../shared/ui/i18n.js';
import type { PlaceDetails } from '@voll-intelligence/genui-contract';
import { PlacesGoogleMap, type NumberedMapPlace } from './google_map.js';
export function MapDialog({
  apiKey,
  places,
  anchorPlace,
  selectedId,
  onSelect,
  onClose,
}: {
  readonly apiKey: string;
  readonly places: readonly NumberedMapPlace[];
  readonly anchorPlace?: PlaceDetails;
  readonly selectedId?: string;
  readonly onSelect: (placeId: string) => void;
  readonly onClose: () => void;
}) {
  const { t } = useGenUiI18n();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  useFocusTrap(panelRef, true);
  useEffect(() => {
    closeRef.current?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', keydown);
    return () => {
      document.removeEventListener('keydown', keydown);
    };
  }, [onClose]);

  // `fixed inset-0` no WebView full-bleed (`viewport-fit=cover`) nasce no topo físico da
  // tela: no telefone o respiro soma a safe-area de cada borda, senão o cabeçalho e o
  // "Fechar" caem sob a status bar / Dynamic Island (#3366). O app é só retrato, então o
  // `sm:` é o desktop, com inset 0.
  return createPortal(
    <div className="genui2 fixed inset-0 z-50 bg-foreground/50 pt-[calc(0.75rem+env(safe-area-inset-top,0px))] pr-[calc(0.75rem+env(safe-area-inset-right,0px))] pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))] pl-[calc(0.75rem+env(safe-area-inset-left,0px))] sm:p-8">
      <button className="absolute inset-0" aria-label={t('common.close')} onClick={onClose} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('consultPlaces.expandedMap')}
        className="relative z-10 mx-auto flex h-full max-w-5xl flex-col rounded-3xl bg-card p-4 shadow-xl"
      >
        <header className="mb-3 flex items-center justify-between gap-4">
          <h2 className="text-lg font-semibold">{t('consultPlaces.expandedMap')}</h2>
          <button
            ref={closeRef}
            type="button"
            className="rounded-full border border-border px-3 py-2 text-sm max-sm:min-h-11"
            onClick={onClose}
          >
            {t('common.close')}
          </button>
        </header>
        <div className="min-h-0 flex-1">
          <PlacesGoogleMap
            apiKey={apiKey}
            places={places}
            anchorPlace={anchorPlace}
            selectedId={selectedId}
            onSelect={onSelect}
            loadingLabel={t('consultPlaces.loadingMap')}
            errorLabel={t('consultPlaces.mapError')}
            retryLabel={t('consultPlaces.retryMap')}
            ariaLabel={t('consultPlaces.mapAria')}
            expanded
          />
        </div>
        {anchorPlace && (
          <p className="mt-3 text-sm text-muted-foreground">
            <span aria-hidden>◆</span> {t('consultPlaces.referencePlace')}: {anchorPlace.name}
          </p>
        )}
      </div>
    </div>,
    document.body,
  );
}
